import { memo, useMemo } from "react";
import { TbTrendingUp } from "react-icons/tb";
import {
    WalletGatedButton,
    DialogAwareContentContainer,
    DialogAwareButtonContainer,
    DurationOptionCard,
    Icon,
    IconButton,
    TokenAmount,
    Tooltip,
} from "@/components";
import { useAnnualProjectedEarnings, useChangeDurationForm } from "@/hooks";
import { LOCK_DURATION_OPTIONS } from "@/config";
import { formatTokenAmountForDisplay } from "@/utils";
import { TOKEN_IDS } from "@/types";
import { formatDisplayCurrency } from "@/utils/format-currency";
import { twMerge } from "tailwind-merge";

interface ChangeDurationFormProps {
    isDialog?: boolean;
    existingDuration: number;
    existingLockAmount: number;
    selectedDuration?: number;
}

interface ChangeDurationOptionProps {
    duration: number;
    principal: number;
    selectedDuration: number;
    onSelect: (duration: number) => void;
    disabled?: boolean;
    className?: string;
}

const ChangeDurationOption = memo(function ChangeDurationOption({
    duration,
    principal,
    selectedDuration,
    onSelect,
    disabled = false,
    className = "",
}: ChangeDurationOptionProps) {
    const { apy, isAPYLoading } = useAnnualProjectedEarnings(duration, principal);
    const isSelected = selectedDuration === duration;
    const isDisabled = disabled || isSelected;

    const classMerge = twMerge(
        isSelected && "border-primary bg-primary/15",
        !isSelected && "border-dark/10 bg-white/5",
        "max-h-auto hover:border-primary/40 hover:bg-primary/5",
        className,
    );

    return (
        <DurationOptionCard
            title={`${duration} week${duration === 1 ? "" : "s"}`}
            subtitle={isAPYLoading ? "—" : `${apy}% APY`}
            onClick={() => {
                if (isDisabled) return;
                onSelect(duration);
            }}
            disabled={isDisabled}
            containerClassName={classMerge}
            titleClassName={twMerge("text-[14px] leading-[18px]", isSelected && "text-primary")}
            subtitleClassName={twMerge("text-[14px] leading-[18px] text-muted", isSelected && "text-dark")}
        />
    );
});

ChangeDurationOption.displayName = "ChangeDurationOption";

export function ChangeDurationForm({
    isDialog,
    existingDuration,
    existingLockAmount,
    selectedDuration,
}: ChangeDurationFormProps) {
    const { duration, durationError, submitChangeDuration, isLoading } = useChangeDurationForm({
        existingDuration,
        existingLockAmount,
        selectedDuration,
    });
    const { apy, isAPYLoading, calculateProjectedEarnings } = useAnnualProjectedEarnings(
        Number(duration.value),
        existingLockAmount,
    );

    const availableDurationOptions = useMemo(
        () => LOCK_DURATION_OPTIONS.filter((option) => option.value >= existingDuration),
        [existingDuration],
    );

    const selectedDurationValue = Number(duration.value);
    const isChangeDurationDisabled = selectedDurationValue <= existingDuration;
    const changeDurationDisabledReason = isChangeDurationDisabled ? "Select a longer duration first." : "";

    const projectedEarnings = useMemo(() => {
        const value = Number(calculateProjectedEarnings(existingLockAmount.toString()));
        if (!Number.isFinite(value)) return "0.00";
        return formatDisplayCurrency(value, 2);
    }, [calculateProjectedEarnings, existingLockAmount, selectedDurationValue]);

    return (
        <form onSubmit={submitChangeDuration} className="text-[#444]">
            <DialogAwareContentContainer isDialog={isDialog}>
                <section className="flex flex-col gap-2">
                    <span className="flex items-center gap-1">
                        <TbTrendingUp className="h-5 w-5 text-secondary" />
                        <span className="text-primary text-lg font-medium leading-6">
                            Boost yield by choosing a longer unlock
                        </span>
                    </span>
                    <p className="text-sm leading-5 text-dark">
                        Your position remains locked until you begin unlock. The unlock completes after the selected
                        waiting period below. Locking longer earns more.
                    </p>
                </section>

                <section className="mt-2 flex flex-col gap-1 text-dark">
                    <div className="flex items-center justify-between text-[16px] leading-7">
                        <span>Locked amount</span>
                        <TokenAmount
                            amount={formatTokenAmountForDisplay(existingLockAmount?.toString())}
                            tokenId={TOKEN_IDS.IUSD}
                            className="text-[16px]"
                            slotProps={{
                                amount: { className: "text-[16px] leading-7" },
                                symbol: { className: "text-[16px] leading-7" },
                            }}
                        />
                    </div>
                    <div className="flex items-center justify-between text-[16px] leading-7">
                        <span>Unlock duration</span>
                        <span>
                            {existingDuration} week{existingDuration === 1 ? "" : "s"}
                        </span>
                    </div>
                </section>

                <section className="mt-4">
                    <div className="text-dark text-xl leading-7">Adjust unlock period:</div>
                    <div className="mt-3 grid grid-cols-3 gap-3">
                        {availableDurationOptions.map((option) => (
                            <ChangeDurationOption
                                key={option.value}
                                duration={option.value}
                                principal={existingLockAmount}
                                selectedDuration={selectedDurationValue}
                                onSelect={duration.onChange}
                            />
                        ))}
                    </div>
                </section>

                <section className="mt-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <div className="inline-flex items-center gap-1 text-lg leading-6 text-secondary">
                        <span>{isAPYLoading ? "—" : `${apy}%`} Projected Net APY</span>
                        <Tooltip
                            title={`
                                Projected return for ${duration.value === 1 ? "a" : duration.value} week${
                                    duration.value === 1 ? "" : "s"
                                } lock based on trailing 7-day averages, current composition of locked depositor tranches, and current composition of infiniFi Portfolio. This figure does not represent or guarantee future results. Inclusive of fees.
                            `}
                        >
                            <span className="inline-block cursor-help">
                                <IconButton size="sm" variant="ghost" className="p-0">
                                    <Icon name="Info" className="h-4 w-4 text-dark" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div className="text-dark text-lg leading-6">
                        Projected earnings: {isAPYLoading ? "—" : projectedEarnings} iUSD
                    </div>
                </section>
                {durationError && <div className="text-red-500 text-sm mt-1">{durationError.message}</div>}
            </DialogAwareContentContainer>

            <DialogAwareButtonContainer isDialog={isDialog}>
                {isChangeDurationDisabled && changeDurationDisabledReason ? (
                    <Tooltip title={changeDurationDisabledReason} placement="top-start" arrow>
                        <span className="inline-flex w-full">
                            <WalletGatedButton
                                className="text-lg py-2.5"
                                type="submit"
                                isLoading={isLoading}
                                fullWidth
                                variant="primary"
                                color="primary"
                                disabled={isChangeDurationDisabled}
                            >
                                Change Duration
                            </WalletGatedButton>
                        </span>
                    </Tooltip>
                ) : (
                    <WalletGatedButton
                        className="text-lg py-2.5"
                        type="submit"
                        isLoading={isLoading}
                        fullWidth
                        variant="primary"
                        color="primary"
                        disabled={isChangeDurationDisabled}
                    >
                        Change Duration
                    </WalletGatedButton>
                )}
            </DialogAwareButtonContainer>
        </form>
    );
}
